import React, { useState, useMemo } from 'react';
import { Table, Input, FormGroup, Label, Col, Row } from 'reactstrap';
import { useQueryRetrieveTransactions } from '../../../services/stores/queries';
import { Transaction } from '../../../types';
import  LoadingInPogress from '../../../components/LoadingInProgress';
import { formatDateYYYYMMDD } from '../../../helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
type SortField = 'date' | 'productName' | 'quantity' | 'uom' | 'transactionType' | 'transactionName' | 'comment';
type SortDirection = 'asc' | 'desc';

const TRANSLATIONS = {
    LOADING: "Chargement des transactions...",
    TRANSACTION_TYPE: "Type",
    TRANSACTION_NAME: "Nom",
    COMMENT: "Remarque",
    DATE: "Date",
    PRODUCT: "Produit",
    QUANTITY: "Quantité",
    UOM: "Unité",
    ALL_TYPES: "Tous",
    ALL_NAMES: "Tous",
};

const TRANSACTION_TYPE_COLORS = {
    'SPRAY PLAN': '#9C27B0',      // Purple
    'FERTIGATION PLAN': '#2196F3', // Blue
    'NEW': '#4CAF50',            // Green
    'TRANSFER+': '#4CAF50',     // Green
    'TRANSFER-': '#F44336',    // Red

    
 //   'SPRAY PLAN': '#4CAF50',      // Green
 //   'FERTIGATION PLAN': '#2196F3', // Blue/
//    'NEW': '#9C27B0',            // Purple
//    'TRANSFER+': '#FF9800',     // Orange
 //   'TRANSFER-': '#F44336',    // Red
  } as const;
  
  const ColorIcon: React.FC<{ type: string }> = ({ type }) => (
    <div
      style={{
        width: '16px',
        height: '16px',
        borderRadius: '50%',
        backgroundColor: TRANSACTION_TYPE_COLORS[type as keyof typeof TRANSACTION_TYPE_COLORS] || '#757575',
        display: 'inline-block'
      }}
    />
  );

export const InventoryTransactions: React.FC = () => {
    const { data: transactions, isLoading, error } = useQueryRetrieveTransactions();
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
    const [typeFilter, setTypeFilter] = useState<string[]>([]);
    const [nameFilter, setNameFilter] = useState<string[]>([]);

    // Get unique transaction types and names for filters
    const uniqueTypes = useMemo(() => {
        if (!transactions) return [];
        return [...new Set(transactions.map(t => t.transactionType))].sort();
    }, [transactions]);

    const uniqueNames = useMemo(() => {
        if (!transactions) return [];
        return [...new Set(transactions.map(t => t.transactionName))].sort();
    }, [transactions]);

    // Sort and filter transactions
    const filteredAndSortedTransactions = useMemo(() => {
        if (!transactions) return [];

        return transactions
            .filter(t => 
                (typeFilter.length === 0 || typeFilter.includes(t.transactionType)) &&
                (nameFilter.length === 0 || nameFilter.includes(t.transactionName))
            )
            .sort((a, b) => {
                const multiplier = sortDirection === 'asc' ? 1 : -1;
                
                if (sortField === 'date') {
                    return multiplier * (new Date(a.date).getTime() - new Date(b.date).getTime());
                }
                if (sortField === 'quantity') {
                    return multiplier * (a.quantity - b.quantity);
                }
                return multiplier * String(a[sortField]).localeCompare(String(b[sortField]));
            });
    }, [transactions, sortField, sortDirection, typeFilter, nameFilter]);

    const handleSort = (field: SortField) => {
        if (field === sortField) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field: SortField) => {
        if (field !== sortField) return <FontAwesomeIcon icon={faSort} className="ms-2 text-muted" />;
        return sortDirection === 'asc' 
            ? <FontAwesomeIcon icon={faSortUp} className="ms-2" />
            : <FontAwesomeIcon icon={faSortDown} className="ms-2" />;
    };

    if (isLoading) return <LoadingInPogress message={TRANSLATIONS.LOADING} />;

    console.log("transactions: " + JSON.stringify(transactions));   

    return (
        <div className="inventory-transactions">

            <Row className="mb-3">
                <Col md={6}>
                    <FormGroup>
                        <Label for="nameFilter">{TRANSLATIONS.TRANSACTION_NAME}</Label>
                        <div className="border p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {uniqueNames.map(name => (
                                <div key={name} className="form-check">
                                    <Input
                                        type="checkbox"
                                        id={`name-${name}`}
                                        checked={nameFilter.includes(name)}
                                        onChange={e => {
                                            setNameFilter(prev => 
                                                e.target.checked 
                                                    ? [...prev, name]
                                                    : prev.filter(n => n !== name)
                                            );
                                        }}
                                    />
                                    <Label check for={`name-${name}`}>
                                        {name}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </FormGroup>
                </Col>
            </Row>

            <Table striped hover responsive className="mt-3">
                <thead>
                    <tr>
                        <th/>
                        <th onClick={() => handleSort('date')} style={{ cursor: 'pointer' }}>
                            {TRANSLATIONS.DATE} {getSortIcon('date')}
                        </th>
                        <th onClick={() => handleSort('productName')} style={{ cursor: 'pointer' }}>
                            {TRANSLATIONS.PRODUCT} {getSortIcon('productName')}
                        </th>
                        <th onClick={() => handleSort('quantity')} style={{ cursor: 'pointer' }}>
                            {TRANSLATIONS.QUANTITY} {getSortIcon('quantity')}
                        </th>
                        <th onClick={() => handleSort('uom')} style={{ cursor: 'pointer' }}>
                            {TRANSLATIONS.UOM} {getSortIcon('uom')}
                        </th>
                        <th onClick={() => handleSort('transactionType')} style={{ cursor: 'pointer' }}>
                            {TRANSLATIONS.TRANSACTION_TYPE} {getSortIcon('transactionType')}
                        </th>
                        <th onClick={() => handleSort('transactionName')} style={{ cursor: 'pointer' }}>
                            {TRANSLATIONS.TRANSACTION_NAME} {getSortIcon('transactionName')}
                        </th>
                        <th onClick={() => handleSort('comment')} style={{ cursor: 'pointer' }}>
                            {TRANSLATIONS.COMMENT} {getSortIcon('comment')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {filteredAndSortedTransactions.map((transaction: Transaction) => (
                        <tr key={transaction.id}>
                            <td>
                                <ColorIcon type={transaction.transactionType} />
                            </td>
                            <td>{formatDateYYYYMMDD(transaction.date)}</td>
                            <td>{transaction.productName}</td>
                            <td>{transaction.quantity}</td>
                            <td>{transaction.uom}</td>
                            <td>{transaction.transactionType}</td>
                            <td>{transaction.transactionName}</td>
                            <td>{transaction.comment}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </div>
    );
};

/*


                <Col md={6}>
                    <FormGroup>
                        <Label for="typeFilter">{TRANSLATIONS.TRANSACTION_TYPE}</Label>
                        <div className="border p-2" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                            {uniqueTypes.map(type => (
                                <div key={type} className="form-check">
                                    <Input
                                        type="checkbox"
                                        id={`type-${type}`}
                                        checked={typeFilter.includes(type)}
                                        onChange={e => {
                                            setTypeFilter(prev => 
                                                e.target.checked 
                                                    ? [...prev, type]
                                                    : prev.filter(t => t !== type)
                                            );
                                        }}
                                    />
                                    <Label check for={`type-${type}`}>
                                        {type}
                                    </Label>
                                </div>
                            ))}
                        </div>
                    </FormGroup>
                </Col>
*/
